// Copyright 2025, University of Maryland, All Rights Reserved
import React, { useState } from 'react';
import { cn } from '@/components/ui';
import { WorkspaceSwitcher } from './components/WorkspaceSwitcher';
import { AppConfigProvider } from './context/AppConfigContext';
import { useOrgWorkspaces } from './hooks/use-workspaces';

// Internal component props (for use within WorkspacesComponent)
export interface WorkspaceSelectorInternalProps {
  selectedOrganizationId?: string;
  selectedWorkspaceId?: string;
  onWorkspaceSelect?: (orgId: string, wsId: string) => void;
  refreshOnSelect?: boolean;  // Whether to refresh the page on workspace selection
  className?: string;
  isGlobalAdmin?: boolean;
  onCreateWorkspace?: () => void;
  canCreateWorkspace?: boolean;
}

// Standalone component props (includes context requirements)
export interface WorkspaceSelectorProps extends WorkspaceSelectorInternalProps {
  app_id: string;
  debug?: boolean;
}

// Internal component that uses context if needed
export const WorkspaceSelectorInternal: React.FC<WorkspaceSelectorInternalProps> = ({
  selectedOrganizationId,
  selectedWorkspaceId,
  onWorkspaceSelect,
  refreshOnSelect = false,
  className,
  onCreateWorkspace,
  canCreateWorkspace = false,
}) => {
  // Fetch workspaces for the selected organization using centralized hook
  const { data: accessibleWorkspaces = [], isLoading } = useOrgWorkspaces(selectedOrganizationId);

  // Internal state for selection if not controlled
  const [internalSelection, setInternalSelection] = useState({
    orgId: selectedOrganizationId || '',
    wsId: selectedWorkspaceId || '',
  });

  const handleWorkspaceSelect = (orgId: string, wsId: string) => {
    // Only process if selecting a different workspace
    if (orgId !== selectedOrganizationId || wsId !== selectedWorkspaceId) {
      if (onWorkspaceSelect) {
        onWorkspaceSelect(orgId, wsId);
      } else {
        setInternalSelection({ orgId, wsId });
      }

      // Optionally refresh the page after selection
      if (refreshOnSelect) {
        // Perform a full page refresh to reload all components with the new workspace context
        window.location.href = window.location.pathname.split('/').slice(0, 2).join('/') || '/';
      }
    }
  };

  const currentOrgId = selectedOrganizationId || internalSelection.orgId;
  const currentWsId = selectedWorkspaceId || internalSelection.wsId;

  return (
    <div className={cn('workspace-selector-component', className)} data-testid="nav-ws-selector">
      <WorkspaceSwitcher
        accessibleWorkspaces={accessibleWorkspaces}
        selectedOrganizationId={currentOrgId}
        selectedWorkspaceId={currentWsId}
        selectWorkspace={handleWorkspaceSelect}
        isLoading={isLoading}
        onCreateWorkspace={onCreateWorkspace}
        canCreateWorkspace={canCreateWorkspace}
      />
    </div>
  );
};

// Standalone component that provides its own context
// Note: This component now relies on a parent QueryClientProvider
// The parent app must wrap this component with QueryClientProvider
export const WorkspaceSelector: React.FC<WorkspaceSelectorProps> = ({
  app_id,
  debug = false,
  ...props
}) => {
  // Note: This selector assumes:
  // 1. The global workspaces API has been initialized via initializeWorkspacesApi()
  // 2. A QueryClientProvider exists in the parent component tree

  return (
    <AppConfigProvider app_id={app_id} debug={debug}>
      <WorkspaceSelectorInternal {...props} />
    </AppConfigProvider>
  );
};